import { AlertTriangle, Hourglass } from "lucide-react";
import { useState } from "react";
import { switchedOff } from "../../lib/http";
import type { Me } from "../../lib/me";
import { FeatureOff } from "../FeatureOff";
import { packageChoices } from "../pid/flow";
import { RoleButton } from "../RequireRole";
import { AUTONOMY_MODES, startRequest, type NewRun, type Refusal, type StartForm as Fields, type Startable } from "./moduleFlow";

const TITLE: Record<Startable, string> = {
  "package.intelligence": "Run a whole package",
  "asset.identity": "Identify a tag",
  "asset.lifecycle": "Check a tag's lifecycle",
  "maintenance.insights": "Maintenance history of a component",
  "site.applicability": "Does a bulletin apply to this site",
  "spec.comparison": "Compare an installed part with a replacement",
  "spec.standardization": "Map a tag onto its data sheet",
};

function Field({ label, hint, children }: { label: string; hint?: string; children: React.ReactNode }) {
  return (
    <label className="stack" style={{ gap: 4, fontSize: 14, fontWeight: 600 }}>
      {label}
      {children}
      {hint && <span className="muted" style={{ fontWeight: 400, fontSize: 12 }}>{hint}</span>}
    </label>
  );
}

/**
 * The packages in this person's scope, from `GET /documents/packages`: their own list when they
 * have one, every listed package for someone who may read all of them.
 */
function PackageField({ me, packages, loading, value, onChange }: {
  me: Me | null | undefined; packages: string[]; loading: boolean; value: string; onChange: (v: string) => void;
}) {
  const choices = packageChoices(me, packages);
  return (
    <Field label="Package">
      <select value={value} onChange={(e) => onChange(e.target.value)}>
        <option value="">{choices.length ? "Choose a package" : loading ? "Loading packages..." : "No packages are listed for you yet"}</option>
        {choices.map((p) => <option key={p} value={p}>{p}</option>)}
      </select>
    </Field>
  );
}

/** The form that starts one module. Validates before sending; the server checks again. */
export function StartForm({
  requestType, me, packages = [], packagesLoading = false, busy, onSubmit, onCancel,
}: {
  requestType: Startable;
  me: Me | null | undefined;
  /** Package names from `GET /documents/packages`. */
  packages?: string[];
  /** The package list has not answered yet: say so rather than "no packages". */
  packagesLoading?: boolean;
  busy: boolean;
  onSubmit: (requestType: Startable, body: NewRun) => void;
  onCancel: () => void;
}) {
  const [fields, setFields] = useState<Fields>({ autonomy_mode: "exception" });
  const [problem, setProblem] = useState<string | null>(null);
  const set = (k: keyof NewRun) => (v: string) => setFields((f) => ({ ...f, [k]: v }));
  const text = (k: keyof NewRun, label: string, placeholder: string, hint?: string) => (
    <Field label={label} hint={hint}>
      <input value={fields[k] ?? ""} onChange={(e) => set(k)(e.target.value)} placeholder={placeholder} />
    </Field>
  );
  const pkg = <PackageField me={me} packages={packages} loading={packagesLoading} value={fields.package ?? ""} onChange={set("package")} />;

  const submit = () => {
    const started = startRequest(requestType, fields);
    if ("problem" in started) return setProblem(started.problem);
    setProblem(null);
    onSubmit(started.requestType, started.body);
  };

  return (
    <form className="card stack" style={{ maxWidth: 720 }} aria-label={TITLE[requestType]} onSubmit={(e) => { e.preventDefault(); submit(); }}>
      <h3 style={{ fontSize: 18 }}>{TITLE[requestType]}</h3>
      {requestType === "package.intelligence" && (
        <>
          {pkg}
          <fieldset className="stack" style={{ gap: 6, border: 0, padding: 0 }}>
            <legend style={{ fontSize: 14, fontWeight: 600, marginBottom: 6 }}>Autonomy</legend>
            {AUTONOMY_MODES.map((m) => (
              <label key={m.value} className={`toggle${fields.autonomy_mode === m.value ? " on" : ""}`}>
                <input type="radio" name="autonomy_mode" value={m.value} checked={fields.autonomy_mode === m.value}
                       onChange={() => set("autonomy_mode")(m.value)} />
                <span><strong>{m.label}</strong> <span className="muted">{m.hint}</span></span>
              </label>
            ))}
          </fieldset>
          {text("limit", "Limit", "all tags", "Stop after this many tags. Leave empty for the whole package.")}
          <p className="muted" style={{ fontSize: 13 }}>
            Package runs spend on paid page reads and research. Each person has one package run at a time.
          </p>
        </>
      )}
      {(requestType === "asset.identity" || requestType === "asset.lifecycle") && (
        <>{pkg}{text("tag", "Tag", "for example PT-403")}</>
      )}
      {requestType === "maintenance.insights" && (
        <>{pkg}{text("component", "Component", "for example combustion chamber", "In the words a site visit report uses.")}</>
      )}
      {requestType === "site.applicability" && (
        <>
          {pkg}
          <Field label="Bulletin text" hint="Paste the part of the bulletin that says which installations it applies to.">
            <textarea rows={5} value={fields.notice ?? ""} onChange={(e) => set("notice")(e.target.value)} />
          </Field>
        </>
      )}
      {requestType === "spec.comparison" && (
        <>
          {text("old_key", "Installed part", "MSO:PT-403", "Register key, written package:tag.")}
          {text("new_key", "Proposed replacement", "MSO:PT-404", "Register key, written package:tag.")}
          {text("equipment_class", "Equipment class", "optional", "Differences are ranked only when a JIP33 data sheet for the class is loaded.")}
        </>
      )}
      {requestType === "spec.standardization" && (
        <>
          {pkg}
          {text("tag", "Tag", "for example PT-403")}
          {text("equipment_class", "Equipment class", "optional", "The register's class wins when it holds one.")}
        </>
      )}
      {problem && <p role="alert" style={{ color: "var(--color-fail)", fontSize: 14 }}>{problem}</p>}
      <div className="row">
        <RoleButton role="engineer" me={me} type="submit" disabled={busy}>{busy ? "Starting" : "Start run"}</RoleButton>
        <button type="button" className="ghost" onClick={onCancel}>Cancel</button>
      </div>
    </form>
  );
}

/** A refused start or decision. A limit or a switched-off module is a notice; anything else is an alert. */
export function RefusalNotice({ refusal }: { refusal: Refusal }) {
  const off = switchedOff(refusal);
  if (off !== null) return <FeatureOff feature="This module" reason={off} />;
  const limit = refusal.kind === "limit";
  return (
    <div className={`state-box${limit ? "" : " fail"}`} role={limit ? "status" : "alert"}>
      {limit ? <Hourglass aria-hidden="true" /> : <AlertTriangle aria-hidden="true" />}
      <div>
        <div className="state-title">{refusal.title}</div>
        <p className="muted">{refusal.detail}</p>
      </div>
    </div>
  );
}
